/**
 * Identity — the signed-in person's live records, keyed on Firebase Auth.
 * The reconciler (session.sync.ts) folds them into the session; a host that
 * reads them elsewhere too (Revent web) builds them once and hands the same
 * streams to mountSession, so one listener set serves both.
 */
import { idTokenUser$, snapshotDoc$, snapshotQuery$ } from "@mhmo91/rx";
import { EventGrant, Grant, User } from "@momo/types";
import { organizationId } from "@momo/views/organization";
import type { Auth } from "firebase/auth";
import { collection, doc, type Firestore, query } from "firebase/firestore";
import {
	combineLatest,
	distinctUntilChanged,
	map,
	of,
	shareReplay,
	switchMap,
} from "rxjs";

export function sessionIdentity({ auth, db }: { auth: Auth; db: Firestore }) {
	const organizationGrants$ = (uid: string) =>
		snapshotQuery$(query(collection(db, "users", uid, "grants"))).pipe(
			map((snapshot) =>
				snapshot.docs.map((row) => ({
					id: row.id,
					...Grant.GrantSchema.parse(row.data()),
				})),
			),
		);

	const authUser$ = idTokenUser$(auth).pipe(
		shareReplay({ bufferSize: 1, refCount: true }),
	);

	// Each identity-keyed stream carries the uid it answered for: a replayed
	// answer of the previous identity reaches a new subscriber before the new
	// listener's first snapshot, and only the carried uid tells them apart.
	const staffUid$ = authUser$.pipe(
		map((user) => (user && !user.isAnonymous ? user.uid : null)),
		distinctUntilChanged(),
	);

	const currentMemberships$ = staffUid$.pipe(
		switchMap((uid) =>
			uid
				? organizationGrants$(uid).pipe(map((grants) => ({ uid, grants })))
				: of({ uid, grants: [] }),
		),
		shareReplay({ bufferSize: 1, refCount: true }),
	);
	// Keyed on the active organization as well, and carrying it: event grants
	// live under one organization's grant, so a switch re-reads them there.
	const currentEventGrants$ = combineLatest([
		staffUid$,
		organizationId.$.pipe(distinctUntilChanged()),
	]).pipe(
		switchMap(([uid, orgId]) =>
			uid && orgId
				? snapshotQuery$(
						query(collection(db, "users", uid, "grants", orgId, "events")),
					).pipe(
						map((snapshot) => ({
							uid,
							orgId,
							grants: snapshot.docs.map((row) => ({
								eventId: row.id,
								...EventGrant.EventGrantSchema.parse(row.data()),
							})),
						})),
					)
				: of({ uid, orgId, grants: [] }),
		),
		shareReplay({ bufferSize: 1, refCount: true }),
	);
	// Metadata changes included: `pending` is read off them, and the server's
	// acknowledgement of a local write changes nothing but its metadata, so
	// without them a record written in this tab reads pending until a reload.
	const currentUserDoc$ = staffUid$.pipe(
		switchMap((uid) =>
			uid
				? snapshotDoc$(doc(db, "users", uid), {
						includeMetadataChanges: true,
					}).pipe(
						map((snapshot) => ({
							uid,
							pending: snapshot.metadata.hasPendingWrites,
							user: snapshot.exists()
								? User.UserSchema.parse(snapshot.data())
								: null,
						})),
					)
				: of({ uid, pending: false, user: null }),
		),
		shareReplay({ bufferSize: 1, refCount: true }),
	);

	return {
		authUser$,
		staffUid$,
		currentMemberships$,
		currentEventGrants$,
		currentUserDoc$,
	};
}

export type SessionIdentity = ReturnType<typeof sessionIdentity>;
